import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { toast } from "sonner";
import { CohortRetention } from "./CohortRetention";
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, ComposedChart
} from "recharts";
import { TrendingUp, TrendingDown, Users, DollarSign, FileText, Award, ArrowUpRight, Share2, Link2, Copy, ExternalLink, Check, Lock, Trash2, Plus, Pencil, Upload, BarChart3 } from "lucide-react";
import { PERIOD_PRESETS, getDateRange } from "./periodUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtC = (v) => {
  const abs = Math.abs(v || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}\u20AC${abs.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const fmtC2 = (v) => {
  const abs = Math.abs(v || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}\u20AC${abs.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_COLORS_CHART = ["#10b981", "#f59e0b", "#ef4444", "#8b5cf6"];
const SECTOR_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#84cc16", "#06b6d4", "#e11d48", "#a855f7", "#64748b"];
const SALE_TYPE_COLORS = ["#0ea5e9", "#f59e0b"];

const PeriodDelta = ({ plus, minus, testId }) => (
  <p className="text-xs mt-0.5" data-testid={testId}>
    <span className="text-emerald-600 font-medium">+{plus || 0}</span> · <span className="text-red-500 font-medium">−{minus || 0}</span> <span className="text-muted-foreground">nel periodo</span>
  </p>
);

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border/60 rounded-lg px-3 py-2 shadow-lg">
      {label && <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>}
      {payload.map((entry, i) => (
        <p key={i} className="text-sm" style={{ color: entry.color }}>
          <span className="font-medium">{entry.name}: </span>
          {formatter ? formatter(entry.value) : fmtC(entry.value)}
        </p>
      ))}
    </div>
  );
};

// Aggregate monthly data into quarters
function toQuarterly(monthlyData, sumKeys, avgKeys = []) {
  const qMap = {};
  for (const m of monthlyData) {
    const [y, mo] = m.month.split("-");
    const q = `${y}-Q${Math.ceil(parseInt(mo) / 3)}`;
    if (!qMap[q]) {
      qMap[q] = { month: q, _count: 0 };
      for (const k of [...sumKeys, ...avgKeys]) qMap[q][k] = 0;
    }
    qMap[q]._count += 1;
    for (const k of sumKeys) qMap[q][k] += (m[k] || 0);
    for (const k of avgKeys) qMap[q][k] += (m[k] || 0);
  }
  // For avg keys, divide by count; for cumulative keys, use last value
  const sorted = Object.values(qMap).sort((a, b) => a.month.localeCompare(b.month));
  for (const q of sorted) {
    for (const k of avgKeys) {
      q[k] = q._count > 0 ? round2(q[k] / q._count) : 0;
    }
    for (const k of sumKeys) q[k] = round2(q[k]);
    delete q._count;
  }
  return sorted;
}

// For cumulative: take last month of each quarter
function toQuarterlyCumulative(monthlyData, cumulativeKeys, otherSumKeys) {
  const qMap = {};
  for (const m of monthlyData) {
    const [y, mo] = m.month.split("-");
    const q = `${y}-Q${Math.ceil(parseInt(mo) / 3)}`;
    if (!qMap[q]) {
      qMap[q] = { month: q, partial: false };
      for (const k of otherSumKeys) qMap[q][k] = 0;
    }
    if (m.partial) qMap[q].partial = true;
    // Cumulative: always overwrite (last month wins since data is sorted)
    for (const k of cumulativeKeys) qMap[q][k] = m[k] || 0;
    for (const k of otherSumKeys) qMap[q][k] += (m[k] || 0);
  }
  const sorted = Object.values(qMap).sort((a, b) => a.month.localeCompare(b.month));
  for (const q of sorted) {
    for (const k of [...cumulativeKeys, ...otherSumKeys]) q[k] = round2(q[k]);
  }
  return sorted;
}

function round2(v) { return Math.round((v || 0) * 100) / 100; }

export default function SalesDashboard({ userRole = "admin" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linksDialog, setLinksDialog] = useState(false);
  const [links, setLinks] = useState([]);
  const [newLinkName, setNewLinkName] = useState("");
  const [newLinkPassword, setNewLinkPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [importing, setImporting] = useState(false);
  const [saleTypeFilter, setSaleTypeFilter] = useState(["New", "Up-Sell"]);
  const [periodPreset, setPeriodPreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [salesRepFilter, setSalesRepFilter] = useState("");
  const [salesReps, setSalesReps] = useState([]);
  // NEW toggles
  const [metricMode, setMetricMode] = useState("arr"); // "arr" | "mrr"
  const [timeGranularity, setTimeGranularity] = useState("monthly"); // "monthly" | "quarterly"

  const loadDashboard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (saleTypeFilter.length < 2) params.set("sale_types", saleTypeFilter.join(","));
      const range = periodPreset === "custom" ? { from: customFrom, to: customTo } : getDateRange(periodPreset);
      if (range.from) params.set("date_from", range.from);
      if (range.to) params.set("date_to", range.to);
      if (salesRepFilter) params.set("rep_id", salesRepFilter);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const [dashRes, linksRes, repsRes] = await Promise.all([
        axios.get(`${API}/sales-dashboard${qs}`),
        axios.get(`${API}/investor-links`).catch(() => ({ data: { links: [] } })),
        axios.get(`${API}/meta/sales-reps`).catch(() => ({ data: { reps: [] } })),
      ]);
      setData(dashRes.data);
      setLinks(linksRes.data.links || []);
      setSalesReps(repsRes.data.reps || []);
    } catch (err) {
      toast.error("Errore nel caricamento dashboard");
    }
    setLoading(false);
  }, [saleTypeFilter, periodPreset, customFrom, customTo, salesRepFilter]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const toggleSaleType = (type) => {
    setSaleTypeFilter(prev => {
      if (prev.includes(type)) {
        return prev.length > 1 ? prev.filter(t => t !== type) : prev;
      }
      return [...prev, type];
    });
  };

  const createLink = async () => {
    if (!newLinkName.trim()) { toast.error("Nome obbligatorio"); return; }
    setCreating(true);
    try {
      const body = { name: newLinkName.trim() };
      if (newLinkPassword) body.password = newLinkPassword;
      await axios.post(`${API}/investor-links`, body);
      toast.success("Link creato!");
      setNewLinkName("");
      setNewLinkPassword("");
      const res = await axios.get(`${API}/investor-links`);
      setLinks(res.data.links || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    setCreating(false);
  };

  const deleteLink = async (linkId) => {
    if (!window.confirm("Revocare questo link? Non sar\u00E0 pi\u00F9 accessibile.")) return;
    try {
      await axios.delete(`${API}/investor-links/${linkId}`);
      toast.success("Link revocato");
      setLinks(prev => prev.filter(l => l.link_id !== linkId));
    } catch (err) { toast.error("Errore nella revoca"); }
  };

  const renameLink = async (linkId) => {
    if (!editName.trim()) return;
    try {
      await axios.put(`${API}/investor-links/${linkId}`, { name: editName.trim() });
      setLinks(prev => prev.map(l => l.link_id === linkId ? { ...l, name: editName.trim() } : l));
      setEditingId(null);
      toast.success("Nome aggiornato");
    } catch (err) { toast.error("Errore"); }
  };

  const importExcel = async () => {
    if (!window.confirm("Importare i dati storici dal file Excel? L'operazione \u00E8 idempotente e non creer\u00E0 duplicati.")) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/admin/import-excel`);
      const s = res.data.stats;
      toast.success(`Import completato: ${s.contracts_imported} contratti, ${s.clients} clienti, ${s.users} sales rep, ${s.products} prodotti importati. ${s.contracts_skipped} gi\u00E0 presenti.`);
      loadDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'import");
    }
    setImporting(false);
  };

  const copyLink = (token, linkId) => {
    const url = `${window.location.origin}/investor/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedId(linkId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Link copiato!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        Impossibile caricare i dati della dashboard.
      </div>
    );
  }

  const { kpis, by_status, by_sector, by_sale_type, leaderboard, monthly_trend, monthly_net = [] } = data;
  const statusWithValues = by_status.filter(s => s.value > 0);
  const isArr = metricMode === "arr";
  const isQ = timeGranularity === "quarterly";
  const metricLabel = isArr ? "ARR" : "MRR";

  // Prepare chart data based on granularity
  const withPartial = (rows) => rows.map(r => ({ ...r, month: r.partial ? `${r.month} (in corso)` : r.month }));
  const sumKeys = ["arr_added", "arr_lost", "arr_reactivated", "net_arr", "mrr_added", "mrr_lost", "mrr_reactivated", "net_mrr", "new_contracts", "value_added"];
  const trendData = withPartial(isQ ? toQuarterlyCumulative(monthly_trend, ["cumulative_arr", "cumulative_mrr"], sumKeys) : monthly_trend);
  const netData = withPartial(isQ ? toQuarterlyCumulative(monthly_net, [], sumKeys) : monthly_net);

  // Churn series: point-in-time base at day before period start; partial (current) period flagged
  const churnData = ((data.churn_series || {})[isQ ? "quarter" : "month"] || []).map(r => {
    const rate = isArr ? r.churn_rate : r.mrr_churn_rate;
    return {
      ...r,
      month: r.partial ? `${r.period} (in corso)` : r.period,
      lost_cancelled: isArr ? r.cancelled : r.mrr_cancelled,
      lost_frozen: isArr ? r.frozen : r.mrr_frozen,
      lost_refunded: isArr ? r.refunded : r.mrr_refunded,
      rate_closed: r.partial ? null : rate,
      rate_partial: r.partial ? rate : null,
      logo_rate_closed: r.partial ? null : r.logo_churn_rate,
      logo_rate_partial: r.partial ? r.logo_churn_rate : null,
    };
  });

  // ARR/MRR field helpers
  const f = (field) => isArr ? field : field.replace("arr", "mrr");
  const cumField = isArr ? "cumulative_arr" : "cumulative_mrr";

  // Toggle button component
  const ToggleGroup = ({ value, onChange, options, testId }) => (
    <div className="flex items-center bg-muted/40 rounded-lg p-0.5" data-testid={testId}>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
            value === opt.value
              ? "bg-background shadow-sm text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid={`${testId}-${opt.value}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-lg font-semibold">Panoramica Vendite</h2>
          <ToggleGroup value={metricMode} onChange={setMetricMode} options={[{value:"arr",label:"ARR"},{value:"mrr",label:"MRR"}]} testId="metric-mode" />
          <ToggleGroup value={timeGranularity} onChange={setTimeGranularity} options={[{value:"monthly",label:"Mensile"},{value:"quarterly",label:"Trimestrale"}]} testId="time-granularity" />
          <ToggleGroup
            value={saleTypeFilter.length === 2 ? "all" : saleTypeFilter[0]}
            onChange={(v) => setSaleTypeFilter(v === "all" ? ["New", "Up-Sell"] : [v])}
            options={[{value:"all",label:"Tutti"},{value:"New",label:"New"},{value:"Up-Sell",label:"Up-Sell"}]}
            testId="sale-type-filter"
          />
          <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={periodPreset} onChange={(e) => setPeriodPreset(e.target.value)} data-testid="dash-filter-period">
            {PERIOD_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
          {periodPreset === "custom" && (
            <div className="flex items-center gap-1">
              <Input type="date" className="h-8 w-[120px] text-xs" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="dash-custom-from" />
              <span className="text-xs text-muted-foreground">&mdash;</span>
              <Input type="date" className="h-8 w-[120px] text-xs" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="dash-custom-to" />
            </div>
          )}
          <select className="h-8 rounded-md border border-input bg-background px-2 text-xs" value={salesRepFilter} onChange={(e) => setSalesRepFilter(e.target.value)} data-testid="dash-filter-rep" style={{ display: (userRole === "admin" || userRole === "csm") ? undefined : "none" }}>
            <option value="">Tutti i sales</option>
            {salesReps.map(r => <option key={r.user_id} value={r.user_id}>{r.name || r.email}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          {userRole === "admin" && (
            <Button variant="outline" className="gap-2 h-9" onClick={importExcel} disabled={importing} data-testid="import-excel-btn">
              <Upload className="h-4 w-4" />
              {importing ? "Importando..." : "Importa Excel"}
            </Button>
          )}
          {userRole === "admin" && (
            <Dialog open={linksDialog} onOpenChange={setLinksDialog}>
              <Button variant="outline" className="gap-2 h-9" onClick={() => setLinksDialog(true)} data-testid="investor-share-btn">
                <Share2 className="h-4 w-4" />
                Link Investitori
                {links.length > 0 && (
                  <span className="ml-1 bg-emerald-100 text-emerald-700 text-xs font-medium px-1.5 py-0.5 rounded-full">{links.length}</span>
                )}
              </Button>
              <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="investor-links-dialog">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Share2 className="h-5 w-5" />
                    Link Investor Dashboard
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                  <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs font-medium text-muted-foreground uppercase">Crea Nuovo Link</p>
                    <Input placeholder="Nome link (es: Round A, Board Meeting)" value={newLinkName} onChange={(e) => setNewLinkName(e.target.value)} data-testid="new-link-name" />
                    <Input type="password" placeholder="Password (opzionale)" value={newLinkPassword} onChange={(e) => setNewLinkPassword(e.target.value)} data-testid="new-link-password" />
                    <Button className="w-full gap-2 h-8 text-sm" onClick={createLink} disabled={creating} data-testid="create-link-btn">
                      <Plus className="h-3.5 w-3.5" /> Crea Link
                    </Button>
                  </div>
                  {links.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground py-4">Nessun link creato.</p>
                  ) : (
                    <div className="space-y-2" data-testid="investor-links-list">
                      {links.map(link => {
                        const url = `${window.location.origin}/investor/${link.share_token}`;
                        const isEditing = editingId === link.link_id;
                        return (
                          <div key={link.link_id} className="border border-border/60 rounded-lg p-3 space-y-2" data-testid={`link-item-${link.link_id}`}>
                            <div className="flex items-center justify-between gap-2">
                              {isEditing ? (
                                <div className="flex items-center gap-1 flex-1">
                                  <Input className="h-7 text-sm" value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && renameLink(link.link_id)} autoFocus data-testid="edit-link-name-input" />
                                  <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => renameLink(link.link_id)}>
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <Link2 className="h-4 w-4 text-emerald-600 shrink-0" />
                                  <span className="font-medium text-sm truncate">{link.name}</span>
                                  {link.has_password && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                                </div>
                              )}
                              <div className="flex items-center gap-1 shrink-0">
                                {!isEditing && (
                                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditingId(link.link_id); setEditName(link.name); }} data-testid={`rename-link-${link.link_id}`}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => copyLink(link.share_token, link.link_id)} data-testid={`copy-link-${link.link_id}`}>
                                  {copiedId === link.link_id ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => window.open(url, '_blank')} data-testid={`open-link-${link.link_id}`}>
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => deleteLink(link.link_id)} data-testid={`delete-link-${link.link_id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span className="font-mono bg-muted px-1.5 py-0.5 rounded truncate max-w-[300px]">{url}</span>
                              <span className="shrink-0">{new Date(link.created_at).toLocaleDateString("it-IT")}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Ogni link mostra ARR, MRR, distribuzione settori e trend di crescita in sola lettura. Nessun dato sensibile.
                  </p>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-emerald-600 uppercase">{metricLabel} Totale Attivo</p>
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-emerald-700 mt-1" data-testid="dash-kpi-arr">{fmtC(isArr ? kpis.total_arr : kpis.total_mrr)}</p>
            <p className="text-xs text-emerald-600" data-testid="dash-kpi-snapshot">al {kpis.snapshot_date ? new Date(kpis.snapshot_date).toLocaleDateString("it-IT") : "—"}</p>
          </CardContent>
        </Card>
        <Card className="bg-sky-50 border-sky-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-sky-700 uppercase">Nuovo Contrattualizzato</p>
              <FileText className="h-4 w-4 text-sky-500" />
            </div>
            <p className="text-2xl font-bold text-sky-800 mt-1" data-testid="dash-kpi-new-contracted">{fmtC(kpis.new_contracted)}</p>
            <p className="text-xs text-sky-600">{kpis.new_count} contratti inseriti nel periodo</p>
          </CardContent>
        </Card>
        <Card className="bg-teal-50 border-teal-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-teal-700 uppercase">Nuovo {metricLabel} Inserito</p>
              <Plus className="h-4 w-4 text-teal-500" />
            </div>
            <p className="text-2xl font-bold text-teal-800 mt-1" data-testid="dash-kpi-new-arr">{fmtC(isArr ? kpis.new_arr : kpis.new_mrr)}</p>
            <p className="text-xs text-teal-600">tutti gli stati, nel periodo</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase">Contratti Attivi</p>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-active">{kpis.active_count}<span className="text-sm font-normal text-muted-foreground ml-1">/ {kpis.total_count}</span></p>
            <PeriodDelta plus={kpis.new_count} minus={kpis.lost_count} testId="dash-kpi-active-delta" />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase">Valore Medio Contratti Attivi</p>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-avg-deal">{fmtC(kpis.avg_deal)}</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-muted-foreground uppercase">Rimborsato</p>
              <DollarSign className="h-4 w-4 text-red-400" />
            </div>
            <p className="text-2xl font-bold text-red-500 mt-1" data-testid="dash-kpi-refunded">{fmtC(isArr ? kpis.total_refunded : kpis.total_refunded / 12)}</p>
            <p className="text-xs text-red-400">{metricLabel} passato a Rimborsato nel periodo · importi rimborsati {fmtC(kpis.refund_amounts)}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-red-600 uppercase">% {metricLabel} persa sui serviti</p>
              <TrendingDown className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold text-red-700 mt-1" data-testid="dash-kpi-churn-rate" title="Serviti = attivi a inizio periodo + acquisiti nel periodo">{(isArr ? kpis.churn_rate : kpis.mrr_churn_rate) ?? "n.d."}{(isArr ? kpis.churn_rate : kpis.mrr_churn_rate) != null ? "%" : ""}</p>
            <p className="text-xs text-red-500" data-testid="dash-kpi-churn-rate-desc">{fmtC(isArr ? kpis.churn_arr_lost : kpis.churn_mrr_lost)} persi / {fmtC(isArr ? kpis.churn_arr_served : kpis.churn_mrr_served)} serviti</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-orange-600 uppercase">% clienti persi sui serviti</p>
              <Users className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-700 mt-1" data-testid="dash-kpi-logo-churn" title="Serviti = attivi a inizio periodo + acquisiti nel periodo">{kpis.logo_churn_rate ?? "n.d."}{kpis.logo_churn_rate != null ? "%" : ""}</p>
            <p className="text-xs text-orange-500" data-testid="dash-kpi-logo-churn-desc">{kpis.logo_churn_count} persi / {kpis.churn_clients_served} serviti</p>
          </CardContent>
        </Card>
      </div>

      {/* KPI Row 2: Licenses & Clients */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Licenze Storiche</p>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-licenses">{kpis.active_licenses}<span className="text-sm font-normal text-muted-foreground ml-1">/ {kpis.total_licenses}</span></p>
            <p className="text-xs text-muted-foreground">attive / storiche</p>
            <PeriodDelta plus={kpis.new_licenses} minus={kpis.lost_licenses} testId="dash-kpi-licenses-delta" />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Clienti Storici</p>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-clients">{kpis.active_clients}<span className="text-sm font-normal text-muted-foreground ml-1">/ {kpis.total_clients}</span></p>
            <p className="text-xs text-muted-foreground">attivi / storici</p>
            <PeriodDelta plus={kpis.new_clients} minus={kpis.logo_churn_count} testId="dash-kpi-clients-delta" />
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Licenze / Contratto</p>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-lic-contract">{kpis.avg_licenses_per_contract}</p>
            <p className="text-xs text-muted-foreground">media attivi</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Licenze / Cliente</p>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-lic-client">{kpis.avg_licenses_per_client}</p>
            <p className="text-xs text-muted-foreground">media attivi</p>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-muted-foreground uppercase">Valore Medio / Licenza</p>
            <p className="text-2xl font-bold mt-1" data-testid="dash-kpi-avg-value-license">{fmtC(isArr ? kpis.avg_value_per_license : kpis.avg_value_per_license / 12)}</p>
            <p className="text-xs text-muted-foreground">{metricLabel} / licenze attive</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1: Cumulative Trend + Status Pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Trend {metricLabel} Cumulativo</CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="arrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} padding={{ left: 10, right: 30 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `\u20AC${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="linear" dataKey={cumField} name={`${metricLabel} attivo a fine periodo`} stroke="#10b981" fill="url(#arrGrad)" strokeWidth={2} dot={{ r: 3, fill: "#10b981" }} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato disponibile</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Contratti firmati nel periodo per stato</CardTitle>
          </CardHeader>
          <CardContent>
            {statusWithValues.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={statusWithValues} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name">
                    {statusWithValues.map((entry, i) => {
                      const colorIdx = by_status.findIndex(s => s.key === entry.key);
                      return <Cell key={entry.key} fill={STATUS_COLORS_CHART[colorIdx >= 0 ? colorIdx : i]} />;
                    })}
                  </Pie>
                  <Tooltip content={<CustomTooltip formatter={(v) => `${v} contratti`} />} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun contratto</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2: By Sector + By Sale Type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{metricLabel} per Settore</CardTitle>
          </CardHeader>
          <CardContent>
            {by_sector.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={by_sector} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `\u20AC${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="sector" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={130} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="arr" name={metricLabel} radius={[0, 4, 4, 0]}>
                    {by_sector.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">New vs Up-Sell <span className="text-xs font-normal text-muted-foreground">venduto nel periodo</span></CardTitle>
          </CardHeader>
          <CardContent>
            {by_sale_type.some(s => s.arr > 0) ? (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={by_sale_type.filter(s => s.arr > 0).map(s => ({ ...s, name: `${s.name} (${s.count})` }))} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="arr" nameKey="name">
                    {by_sale_type.filter(s => s.arr > 0).map((entry, i) => (
                      <Cell key={entry.name} fill={SALE_TYPE_COLORS[i % SALE_TYPE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sales Rep Leaderboard */}
      {(userRole === "admin" || userRole === "csm") && (
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-amber-500" />
              <CardTitle className="text-base">Classifica Sales</CardTitle>
              <span className="text-xs text-muted-foreground">Venduto nel periodo · Portafoglio attivo al {kpis.snapshot_date ? new Date(kpis.snapshot_date).toLocaleDateString("it-IT") : "—"}</span>
            </div>
          </CardHeader>
          <CardContent>
            {leaderboard.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="leaderboard">
                {leaderboard.map((rep, i) => (
                  <div key={rep.rep_id} className={`flex items-center gap-3 p-3 rounded-lg ${i === 0 ? "bg-amber-50 border border-amber-200" : "bg-muted/30"}`} data-testid={`leaderboard-rep-${i}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${i === 0 ? "bg-amber-500 text-white" : i === 1 ? "bg-gray-400 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-muted text-muted-foreground"}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{rep.name}</p>
                      <p className="text-xs text-muted-foreground">Valore medio attivi: {fmtC(rep.avg_deal)}</p>
                    </div>
                    <div className="text-right shrink-0" data-testid={`leaderboard-sold-${i}`}>
                      <p className="font-bold text-sky-700">{fmtC(isArr ? rep.sold_arr : rep.sold_mrr)}</p>
                      <p className="text-[11px] text-muted-foreground">venduto · {rep.sold_contracts} contr.</p>
                    </div>
                    <div className="text-right shrink-0 pl-3 border-l border-border/60" data-testid={`leaderboard-portfolio-${i}`}>
                      <p className="font-bold text-emerald-600">{fmtC(isArr ? rep.arr : rep.mrr)}</p>
                      <p className="text-[11px] text-muted-foreground">portafoglio · {rep.contracts} contr.</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-8 text-center text-muted-foreground text-sm">Nessun rep con contratti attivi</div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Charts: New Contracts + New ARR/MRR */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Nuovi Contratti per {isQ ? "Trimestre" : "Mese"}</CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip formatter={(v) => `${v}`} />} />
                  <Bar dataKey="new_contracts" name="Nuovi Contratti" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Nuovo {metricLabel} {isQ ? "Trimestrale" : "Mensile"}</CardTitle>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `\u20AC${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey={f("arr_added")} name={`Nuovo ${metricLabel}`} fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ARR perso + Churn Rate */}
      <CohortRetention cohorts={data.cohorts} />
      <p className="text-xs text-muted-foreground -mb-2" data-testid="churn-definition-note">
        Serviti = attivi a inizio periodo + acquisiti nel periodo (stato alla data, ogni cliente contato una volta). Tasso = persi nel periodo / serviti, inclusi gli acquisiti e persi nello stesso periodo. Congelato conta come perso; le riattivazioni sono registrate a parte. Il periodo in corso è indicato come "(in corso)" e non fa parte della linea di trend.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dash-row-churn">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{metricLabel} Perso per {isQ ? "Trimestre" : "Mese"}</CardTitle>
          </CardHeader>
          <CardContent data-testid="lost-arr-chart">
            {churnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={churnData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="lost_cancelled" name="Annullato" stackId="base" fill="#ef4444">
                    {churnData.map((r, i) => <Cell key={i} fillOpacity={r.partial ? 0.4 : 1} />)}
                  </Bar>
                  <Bar dataKey="lost_frozen" name="Congelato" stackId="base" fill="#f59e0b">
                    {churnData.map((r, i) => <Cell key={i} fillOpacity={r.partial ? 0.4 : 1} />)}
                  </Bar>
                  <Bar dataKey="lost_refunded" name="Rimborsato" stackId="base" fill="#8b5cf6" radius={[4, 4, 0, 0]}>
                    {churnData.map((r, i) => <Cell key={i} fillOpacity={r.partial ? 0.4 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">% {metricLabel} persa sui serviti {isQ ? "(trimestrale)" : "(mensile)"}</CardTitle>
          </CardHeader>
          <CardContent data-testid="churn-rate-chart">
            {churnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={churnData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} stroke="hsl(var(--muted-foreground))" padding={{ left: 20, right: 30 }} />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<CustomTooltip formatter={(v) => (v === null || v === undefined ? "n.d." : `${v}%`)} />} />
                  <Line type="linear" dataKey="rate_closed" name="% persa sui serviti" stroke="#ef4444" strokeWidth={2} dot={{ r: 4, fill: "#ef4444" }} connectNulls={false} isAnimationActive={false} />
                  <Line type="linear" dataKey="rate_partial" name="In corso (parziale)" stroke="#ef4444" dot={{ r: 5, fill: "#fecaca", stroke: "#ef4444", strokeWidth: 2, strokeOpacity: 1 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Clienti persi + Logo Churn Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dash-row-logo-churn">
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Clienti Persi {isQ ? "per Trimestre" : "per Mese"}</CardTitle>
          </CardHeader>
          <CardContent data-testid="lost-clients-chart">
            {churnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={churnData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
                  <Tooltip content={<CustomTooltip formatter={(v) => `${v} clienti`} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="logo_churn" name="Clienti persi" fill="#f97316" radius={[4, 4, 0, 0]}>
                    {churnData.map((r, i) => <Cell key={i} fillOpacity={r.partial ? 0.4 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">% clienti persi sui serviti {isQ ? "(trimestrale)" : "(mensile)"}</CardTitle>
          </CardHeader>
          <CardContent data-testid="logo-churn-rate-chart">
            {churnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={churnData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} interval={0} stroke="hsl(var(--muted-foreground))" padding={{ left: 20, right: 30 }} />
                  <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v}%`} />
                  <Tooltip content={<CustomTooltip formatter={(v) => (v === null || v === undefined ? "n.d." : `${v}%`)} />} />
                  <Line type="linear" dataKey="logo_rate_closed" name="% clienti persi sui serviti" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: "#f97316" }} connectNulls={false} isAnimationActive={false} />
                  <Line type="linear" dataKey="logo_rate_partial" name="In corso (parziale)" stroke="#f97316" dot={{ r: 5, fill: "#fed7aa", stroke: "#f97316", strokeWidth: 2, strokeOpacity: 1 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Net ARR */}
      <div className="grid grid-cols-1 gap-4">
        <Card className="border-border/60">
          <CardContent className="pt-6">
            <CardTitle className="text-base mb-4">Net {metricLabel} {isQ ? "Trimestrale" : "Mensile"}</CardTitle>
            {netData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={netData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `\u20AC${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `\u20AC${v.toLocaleString('it-IT')}`} />
                  <Legend />
                  <Bar dataKey={f("arr_added")} name={`Nuovo ${metricLabel}`} fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey={f("arr_lost")} name={`${metricLabel} Perso`} fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey={f("arr_reactivated")} name={`${metricLabel} Riattivato`} fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  <Line type="linear" dataKey={isArr ? "net_arr" : "net_mrr"} name={`Net ${metricLabel}`} stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">Nessun dato</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
